import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { catchError, EMPTY } from 'rxjs';

import { Individual } from '../models';
import { IndividualService } from '../services/individual.service';

/**
 * Resolver for Individual contact routes.
 * Pre-loads individual contact data before navigation completes.
 * 
 * Usage in routing:
 * {
 *   path: 'Individual/:individualId',
 *   resolve: { individual: individualResolver }
 * }
 * 
 * Access in component:
 * this.route.data.subscribe(data => {
 *   const individual = data['individual'];
 * });
 */
export const individualResolver: ResolveFn<Individual.Summary> = (route) => {
  const individualService = inject(IndividualService);
  const router = inject(Router);
  const individualId = route.paramMap.get('individualId');

  if (!individualId) {
    router.navigate(['/error'], {
      state: {
        title: 'Invalid Contact',
        message: 'No contact ID provided.',
        backRoute: ['/Contacts'],
        backLabel: 'Back to Contacts'
      }
    });
    return EMPTY;
  }

  return individualService.getSummary(individualId).pipe(
    catchError((error) => {
      router.navigate(['/error'], {
        state: {
          title: 'Contact Not Found',
          message: 'The individual contact could not be loaded. Please try again or contact support if the issue persists.',
          backRoute: ['/Contacts'],
          backLabel: 'Back to Contacts'
        }
      });
      return EMPTY;
    })
  );
};
